import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import api from '../services/api'
import type { Usuario, LoginResponse } from '../types'

interface AuthContextType {
  user: Usuario | null
  isAuthenticated: boolean
  loading: boolean
  login: (email: string, senha: string) => Promise<LoginResponse>
  logout: () => Promise<void>
  updateUser: (user: Usuario) => void
  isAdmin: boolean
  isGerente: boolean
  isVendedor: boolean
  hasModule: (module: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<Usuario | null>(null)
  const [loading, setLoading] = useState(true)

  const isAuthenticated = !!user
  const isAdmin = user?.tipoUsuario === 'ADMIN'
  const isGerente = user?.tipoUsuario === 'GERENTE'
  const isVendedor = user?.tipoUsuario === 'VENDEDOR'

  const hasModule = useCallback(
    (module: string) => {
      if (isAdmin) return true
      return user?.modulos?.includes(module) ?? false
    },
    [user, isAdmin]
  )

  // Carregar usuário ao inicializar
  useEffect(() => {
    const loadUser = async () => {
      const token = localStorage.getItem('accessToken')
      if (!token) {
        setLoading(false)
        return
      }

      try {
        const response = await api.get('/auth/me')
        setUser(response.data)
      } catch (error) {
        console.error('Erro ao carregar usuário:', error)
        localStorage.clear()
      } finally {
        setLoading(false)
      }
    }

    loadUser()
  }, [])

  // Auto-refresh do token
  useEffect(() => {
    if (!user) return

    const expiresAt = localStorage.getItem('expiresAt')
    if (!expiresAt) return

    const expirationTime = new Date(expiresAt).getTime()
    const now = Date.now()
    const timeUntilRefresh = expirationTime - now - 2 * 60 * 1000 // 2 min antes

    if (timeUntilRefresh <= 0) return

    const timeout = setTimeout(async () => {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) return

      try {
        const response = await api.post('/auth/refresh', { refreshToken })
        const { accessToken, refreshToken: newRefreshToken, expiresAt: newExpiresAt } = response.data

        localStorage.setItem('accessToken', accessToken)
        localStorage.setItem('refreshToken', newRefreshToken)
        localStorage.setItem('expiresAt', newExpiresAt)
      } catch (error) {
        console.error('Erro ao renovar token:', error)
      }
    }, timeUntilRefresh)

    return () => clearTimeout(timeout)
  }, [user])

  const login = async (email: string, senha: string): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', { email, senha })
      const data = response.data

      if (data.sucesso && data.accessToken && data.usuario) {
        localStorage.setItem('accessToken', data.accessToken)
        localStorage.setItem('refreshToken', data.refreshToken!)
        localStorage.setItem('expiresAt', data.expiresAt!)

        if (data.usuario.lojaId) {
          localStorage.setItem('lojaId', data.usuario.lojaId.toString())
        }

        setUser(data.usuario)
      }

      return data
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { message?: string } } }
      return {
        sucesso: false,
        mensagem: axiosError.response?.data?.message || 'Erro ao fazer login',
      }
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.error('Erro ao fazer logout:', error)
    } finally {
      localStorage.clear()
      setUser(null)
    }
  }

  const updateUser = (updatedUser: Usuario) => {
    setUser(updatedUser)
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        loading,
        login,
        logout,
        updateUser,
        isAdmin,
        isGerente,
        isVendedor,
        hasModule,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
